import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { OptionRow } from "@/components/ui/option-row";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Textarea } from "@/components/ui/textarea";
import { useT } from "@/lib/i18n/use-t";
import { T } from "./deviation-decision-card.i18n";

/**
 * QL-313 — the advisor's deviation decision card, shared by the compliance
 * charts (volatility, equity share).
 *
 * One card, one shape: Accept / Block as a radio group with a helper line per
 * option, a justification textarea that appears for Accept, and a Save (plus
 * Clear when a decision is stored) row that is hidden on a locked instance —
 * the service refuses the write there anyway, so offering it only earns a
 * 422 toast. The caller owns the state and the mutation; the card owns the
 * markup, so the two charts (and any third measure) cannot drift apart. The
 * pre-push review on QL-314 found three near-verbatim copies of this card;
 * the custom-portfolio panel keeps its own until QL-493 deletes it.
 *
 * The card owns its words (sidecar), in the compliance charts' wording. A
 * caller whose decision reads differently (the mandate check) overrides the
 * hints and the justification field through `labels`.
 */

export type DeviationDecisionValue = "accepted" | "do_not_proceed";

/** Optional overrides of the card's own wording; omit for the compliance charts' text. */
export type DeviationDecisionCardLabels = {
  acceptHint?: string;
  doNotProceedHint?: string;
  justificationLabel?: string;
  justificationPlaceholder?: string;
};

type DeviationDecisionCardProps = {
  /** Prefix for the control ids, unique per goal (`vc-${goalId}`). */
  idPrefix: string;
  labels?: DeviationDecisionCardLabels;
  /** The option the advisor has picked, or "" for none yet. */
  value: DeviationDecisionValue | "";
  onValueChange: (value: DeviationDecisionValue) => void;
  justification: string;
  onJustificationChange: (value: string) => void;
  /** Accept needs a non-empty justification (the compliance charts' rule). Default true. */
  justificationRequired?: boolean;
  /** A decision is stored server-side, so Clear is offered. */
  hasStoredDecision: boolean;
  onSave: (value: DeviationDecisionValue) => void;
  onClear: () => void;
  isSaving?: boolean;
  /** True on a locked (submitted / completed) instance: shown, never edited. */
  disabled?: boolean;
};

export function DeviationDecisionCard({
  idPrefix,
  labels,
  value,
  onValueChange,
  justification,
  onJustificationChange,
  justificationRequired = true,
  hasStoredDecision,
  onSave,
  onClear,
  isSaving = false,
  disabled = false,
}: DeviationDecisionCardProps) {
  const t = useT(T);
  const acceptHint = labels?.acceptHint ?? t.acceptHint;
  const doNotProceedHint = labels?.doNotProceedHint ?? t.doNotProceedHint;
  const canSave =
    value !== "" && !(justificationRequired && value === "accepted" && !justification.trim());

  return (
    <div className="space-y-3 border rounded-lg p-4">
      <Label className="text-base">{t.title}</Label>
      <RadioGroup
        value={value}
        onValueChange={(next) => onValueChange(next as DeviationDecisionValue)}
        disabled={disabled}
      >
        <OptionRow align="start">
          <RadioGroupItem value="accepted" id={`${idPrefix}-accept`} className="mt-0.5" />
          <Label htmlFor={`${idPrefix}-accept`} className="font-normal cursor-pointer">
            {t.accept}
            <span className="block text-xs text-muted-foreground">{acceptHint}</span>
          </Label>
        </OptionRow>
        <OptionRow align="start">
          <RadioGroupItem value="do_not_proceed" id={`${idPrefix}-block`} className="mt-0.5" />
          <Label htmlFor={`${idPrefix}-block`} className="font-normal cursor-pointer">
            {t.doNotProceed}
            <span className="block text-xs text-muted-foreground">{doNotProceedHint}</span>
          </Label>
        </OptionRow>
      </RadioGroup>

      {value === "accepted" && (
        <div className="space-y-2">
          <Label htmlFor={`${idPrefix}-justification`}>
            {labels?.justificationLabel ?? t.justificationLabel}
          </Label>
          <Textarea
            id={`${idPrefix}-justification`}
            value={justification}
            onChange={(e) => onJustificationChange(e.target.value)}
            placeholder={labels?.justificationPlaceholder ?? t.justificationPlaceholder}
            rows={3}
            disabled={disabled}
          />
        </div>
      )}

      {!disabled && (
        <div className="flex justify-end gap-2">
          {hasStoredDecision && (
            <Button variant="outline" size="sm" onClick={onClear} loading={isSaving}>
              {t.clearDecision}
            </Button>
          )}
          <Button
            size="sm"
            onClick={() => value && onSave(value)}
            loading={isSaving}
            disabled={!canSave}
          >
            {t.save}
          </Button>
        </div>
      )}
    </div>
  );
}
